import React from "react";
import { Box, Paper } from "@mui/material";
import { Image } from "@/types/homepage";
import styles from "./PartnerCard.module.css";
import { RichTextContent, CtaButton } from "@/components";

interface PartnerCardData {
  partner_logo?: Image;
  partner_logo_alt?: string;
  partner_text?: string;
  partner_btn_label?: string;
  partner_btn_url?: string;
}

interface PartnerCardProps {
  data?: PartnerCardData;
}

const PartnerCard: React.FC<PartnerCardProps> = ({ data }) => {
  // Safety check: return null if data is missing or empty
  if (!data || Object.keys(data).length === 0) {
    return null;
  }

  const {
    partner_logo,
    partner_logo_alt = "",
    partner_text,
    partner_btn_label,
    partner_btn_url,
  } = data;

  // Get the appropriate image URL and dimensions
  const getImageData = () => {
    if (!partner_logo) return null;

    // Check if it's a RasterImage with sizes
    if ("sizes" in partner_logo) {
      const mediumUrl = partner_logo.sizes.medium || partner_logo.sizes.large || partner_logo.sizes.original;
      
      if (mediumUrl) {
        return {
          url: mediumUrl,
          width: partner_logo.dimensions.width,
          height: partner_logo.dimensions.height,
        };
      }
    }

    // Fallback to SVGImage (has url property)
    if ("url" in partner_logo) {
      return {
        url: partner_logo.url,
        width: partner_logo.dimensions.width,
        height: partner_logo.dimensions.height,
      };
    }

    return null;
  };

  const imageData = getImageData();

  // Check if the URL is external
  const isExternalLink = (url?: string) => {
    if (!url) return false;
    return url.startsWith("http://") || url.startsWith("https://");
  };

  return (
    <Paper elevation={2} className={styles.partnerCard}>
      <Box className={styles.cardContent}>
        {/* Partner Logo */}
        {imageData && (
          <Box className={styles.logoContainer}>
            <img
              src={imageData.url}
              alt={partner_logo_alt}
              width={imageData.width}
              height={imageData.height}
              className={styles.logo}
            />
          </Box>
        )}

        {/* Partner Text */}
        {partner_text && (
          <RichTextContent
            content={partner_text}
            className={styles.partnerText}
          />
        )}

        {/* Partner Button */}
        {partner_btn_label && partner_btn_url && (
          <Box className={styles.buttonContainer}>
            <CtaButton
              href={partner_btn_url}
              target={isExternalLink(partner_btn_url) ? "_blank" : undefined}
              rel={
                isExternalLink(partner_btn_url)
                  ? "noopener noreferrer"
                  : undefined
              }
            >
              {partner_btn_label}
            </CtaButton>
          </Box>
        )}
      </Box>
    </Paper>
  );
};

export default PartnerCard;
